import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  updateEmail,
  updatePassword,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase';
import { authenticateYouTube } from '../services/youtubeAuthService';
import { storeYouTubeToken } from '../services/youtubeTokenService';
import { syncUserDocument } from '../services/userManagement/userSync';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email, password, displayName, skipYouTubeAuth = false) => {
    // Step 1: Create Firebase account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(userCredential.user, { displayName });
    }

    // Step 2: Sync user document in Firestore (creates if doesn't exist, updates lastLoginAt)
    try {
      await syncUserDocument(userCredential.user);
    } catch (error) {
      // If Firestore document creation fails, log but don't block signup
      console.error('Failed to sync user document in Firestore:', error);
      // Don't throw - allow user to sign up, document can be created later
    }

    // Step 3: Authenticate with YouTube (if not skipped)
    if (!skipYouTubeAuth) {
      try {
        const result = await authenticateYouTube();
        const accessToken = result.accessToken || result; // Handle both old and new format
        const expiresIn = result.expiresIn || 3600;
        await storeYouTubeToken(userCredential.user.uid, accessToken, expiresIn);
      } catch (error) {
        // If YouTube auth fails, still allow signup but log the error
        console.warn('YouTube authentication failed during signup:', error);
        // Don't throw - allow user to sign up without YouTube auth
      }
    }

    return userCredential;
  };

  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Sync user document in Firestore (creates if doesn't exist, updates lastLoginAt, auto-promotes admin)
    try {
      await syncUserDocument(userCredential.user);
    } catch (error) {
      console.error('Failed to sync user document in Firestore:', error);
      // Don't throw - allow login to proceed
    }
    
    return userCredential;
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    
    // Sync user document in Firestore (creates if doesn't exist, updates lastLoginAt)
    try {
      await syncUserDocument(userCredential.user);
    } catch (error) {
      console.error('Failed to sync user document in Firestore:', error);
    }
    
    return userCredential;
  };

  const logout = () => {
    return signOut(auth);
  };

  const updateUserProfile = async (displayName) => {
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }
    return updateProfile(auth.currentUser, { displayName });
  };

  const updateUserEmail = async (newEmail) => {
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }
    return updateEmail(auth.currentUser, newEmail);
  };

  const updateUserPassword = async (newPassword) => {
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }
    return updatePassword(auth.currentUser, newPassword);
  };

  const reauthenticateUser = async (password) => {
    if (!auth.currentUser || !auth.currentUser.email) {
      throw new Error('No user is currently signed in');
    }
    const credential = EmailAuthProvider.credential(
      auth.currentUser.email,
      password
    );
    return reauthenticateWithCredential(auth.currentUser, credential);
  };

  const deleteUserAccount = async (password) => {
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }
    // Reauthenticate before deleting
    await reauthenticateUser(password);
    return deleteUser(auth.currentUser);
  };

  useEffect(() => {
    try {
      const unsubscribe = onAuthStateChanged(
        auth, 
        async (user) => {
          // Sync user document on auth state change (login)
          if (user) {
            try {
              await syncUserDocument(user);
            } catch (error) {
              console.error('Failed to sync user document on auth state change:', error);
              // Don't block auth state change
            }
          }
          
          setCurrentUser(user);
          setLoading(false);
        },
        (error) => {
          console.error('Auth state error:', error);
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Auth initialization error:', error);
      setLoading(false);
    }
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    signInWithGoogle,
    logout,
    updateUserProfile,
    updateUserEmail,
    updateUserPassword,
    reauthenticateUser,
    deleteUserAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};



